/**
 * Stream Events Type Definitions
 *
 * Type-safe definitions for Server-Sent Events (SSE) and custom window events
 * used in the Chat feature for real-time message streaming.
 *
 * @module StreamEvents
 */

import { logger } from '@ui/utils/logger';
import type { StreamPacket } from '@packages/types';

// =============================================================================
// Custom Window Event Types
// =============================================================================

/**
 * Event names for chat stream custom events
 */
export const STREAM_EVENT_NAMES = {
    START: 'chat-stream-start',
    FINAL: 'chat-stream-final',
    ERROR: 'chat-stream-error',
    CLOSED: 'chat-stream-closed',
    CANCELLED: 'chat-stream-cancelled',
    TIMEOUT: 'chat-stream-timeout',
} as const;

/**
 * Type for stream event names
 */
export type StreamEventName = (typeof STREAM_EVENT_NAMES)[keyof typeof STREAM_EVENT_NAMES];

/**
 * Detail payload for custom stream events
 */
export interface StreamEventDetail {
    conversationId: string;
}

/**
 * Custom event type for chat stream events
 * Extends the native CustomEvent with our specific detail type
 */
export interface ChatStreamEvent extends CustomEvent<StreamEventDetail> {
    type: StreamEventName;
    detail: StreamEventDetail;
}

/**
 * Type guard to check if an event is a ChatStreamEvent
 */
export function isChatStreamEvent(event: Event): event is ChatStreamEvent {
    return (
        event instanceof CustomEvent &&
        typeof event.detail === 'object' &&
        event.detail !== null &&
        'conversationId' in event.detail
    );
}

// =============================================================================
// SSE Event Handler Types
// =============================================================================

/**
 * Response from SSE connection open
 * Based on @microsoft/fetch-event-source Response type
 */
export interface SSEOpenResponse {
    ok: boolean;
    status: number;
    statusText: string;
    headers: Headers;
}

/**
 * Message event from SSE stream
 * Based on @microsoft/fetch-event-source EventSourceMessage type
 */
export interface SSEMessageEvent {
    /**
     * The event ID, if any
     */
    id?: string;

    /**
     * The event type, if any
     */
    event?: string;

    /**
     * The data field of the message
     */
    data: string;

    /**
     * The retry field, if any
     */
    retry?: number;
}

/**
 * Type definition for backend timeout stages
 */
export type BackendTimeoutStage = 'timeout' | 'inactivity_timeout';

/**
 * Type guard for backend timeout stages
 */
export function isBackendTimeoutStage(stage: unknown): stage is BackendTimeoutStage {
    return typeof stage === 'string' && (stage === 'timeout' || stage === 'inactivity_timeout');
}

/**
 * Parsed SSE message data
 * After JSON parsing the data field
 */
export interface ParsedSSEMessage {
    packet: StreamPacket;
    raw: string;
}

/**
 * Error types that can occur during SSE streaming
 */
export type SSEErrorType =
    | 'connection_failed'
    | 'parse_error'
    | 'abort_error'
    | 'http_error'
    | 'unknown_error'
    | 'event_dispatch_failed'
    | 'timeout'
    | 'cancelled_by_user';

/**
 * Structured error information for SSE errors
 */
export interface SSEError {
    type: SSEErrorType;
    message: string;
    originalError?: unknown;
    conversationId?: string;
    timestamp: number;
}

/**
 * Callback function types for SSE event handlers
 */
export interface SSEEventHandlers {
    /**
     * Called when the connection is successfully opened
     * @param response - HTTP response object
     */
    onOpen?: (response: SSEOpenResponse) => void | Promise<void>;

    /**
     * Called for each message received from the stream
     * @param event - The SSE message event
     */
    onMessage?: (event: SSEMessageEvent) => void;

    /**
     * Called when an error occurs
     * @param error - The error that occurred
     */
    onError?: (error: unknown) => void;

    /**
     * Called when the stream is closed (successfully or due to error)
     */
    onClose?: () => void;
}

// =============================================================================
// Stream Status Types
// =============================================================================

/**
 * Current status of a stream connection
 */
export type StreamStatus = 'idle' | 'connecting' | 'streaming' | 'completed' | 'error' | 'aborted';

/**
 * Stream connection metadata
 */
export interface StreamMetadata {
    conversationId: string;
    correlationId: string;
    patientId?: string | null;
    startedAt: number;
    completedAt?: number;
    status: StreamStatus;
    error?: SSEError;
}

// =============================================================================
// Helper Types for Event Dispatching
// =============================================================================

/**
 * Options for dispatching custom stream events
 */
export interface DispatchStreamEventOptions {
    eventName: StreamEventName;
    conversationId: string;
    onError?: (error: unknown) => void;
}

/**
 * Type-safe event dispatcher function
 */
export type StreamEventDispatcher = (options: DispatchStreamEventOptions) => void;

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Type guard to check if an error is an AbortError
 */
export function isAbortError(error: unknown): error is Error & { name: 'AbortError' } {
    return error instanceof Error && error.name === 'AbortError';
}

/**
 * Type guard to check if an error is an HTTP error
 */
export function isHTTPError(error: unknown): error is Error & { status: number } {
    return error instanceof Error && 'status' in error && typeof (error as { status: unknown }).status === 'number';
}

/**
 * Type guard to check if a value is a valid SSE message event
 */
export function isValidSSEMessage(event: unknown): event is SSEMessageEvent {
    return (
        typeof event === 'object' &&
        event !== null &&
        'data' in event &&
        typeof (event as SSEMessageEvent).data === 'string'
    );
}

/**
 * Type guard to check if parsed JSON is a valid StreamPacket
 */
export function isStreamPacket(value: unknown): value is StreamPacket {
    if (typeof value !== 'object' || value === null) return false;

    const packet = value as Record<string, unknown>;

    // StreamPacket can have an optional 'response' field
    if (!('response' in packet)) return true; // Empty packet is valid

    const response = packet.response;
    if (typeof response !== 'object' || response === null) return false;

    // If response exists, it can have optional fields, so we just check it's an object
    return true;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Parse SSE message data safely
 * @param event - The SSE message event
 * @returns Parsed message or null if invalid
 */
export function parseSSEMessage(event: SSEMessageEvent): ParsedSSEMessage | null {
    try {
        if (!event.data || event.data.trim() === '') return null;

        // Handle stream completion signal
        if (event.data.trim() === '[DONE]') return null;

        const parsed: unknown = JSON.parse(event.data);

        if (!isStreamPacket(parsed)) {
            logger.error('[SSE] Invalid packet structure', parsed);
            return null;
        }

        return {
            packet: parsed,
            raw: event.data,
        };
    } catch (error) {
        logger.error('[SSE] Failed to parse message', error);
        return null;
    }
}

/**
 * Create a structured SSE error object
 * @param type - The type of error
 * @param error - The original error
 * @param conversationId - Optional conversation ID
 * @returns Structured error object
 */
export function createSSEError(type: SSEErrorType, error: unknown, conversationId?: string): SSEError {
    return {
        type,
        message: error instanceof Error ? error.message : String(error),
        originalError: error,
        conversationId,
        timestamp: Date.now(),
    };
}

/**
 * Type-safe custom event dispatcher
 * @param options - Dispatch options
 */
export function dispatchStreamEvent({ eventName, conversationId, onError }: DispatchStreamEventOptions): void {
    try {
        const event = new CustomEvent(eventName, {
            detail: { conversationId },
        });
        window.dispatchEvent(event);
    } catch (error) {
        logger.error(`[SSE] Failed to dispatch ${eventName} event`, error);
        onError?.(error);
    }
}

// =============================================================================
// Exports
// =============================================================================

/**
 * All stream event types and utilities
 */
export const StreamEventTypes = {
    STREAM_EVENT_NAMES,
    isChatStreamEvent,
    isAbortError,
    isHTTPError,
    isValidSSEMessage,
    isStreamPacket,
    parseSSEMessage,
    createSSEError,
    dispatchStreamEvent,
} as const;

export default StreamEventTypes;
